import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { jsPDF } from "jspdf"; // For PDF generation

function Invoices() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Initial sort settings: sort by orderDate descending
  const [sortField, setSortField] = useState("orderDate");
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    const authDataString = localStorage.getItem("authData");
    //console.log("[Invoices] Retrieved authData:", authDataString);
    if (!authDataString) {
      setError("Not logged in.");
      return;
    }
    const parsedAuthData = JSON.parse(authDataString);
    const userId = parsedAuthData?.user?.sub;
    //console.log("[Invoices] userId:", userId);
    if (!userId) {
      setError("User ID not found in authData.");
      return;
    }
    fetchOrders(userId);
  }, []);

  // Helper function: Convert UTC date string to local date string.
  const convertUTCToLocal = (utcString) => {
    return new Date(utcString).toLocaleString();
  };

  // Helper: Compare two orders based on the given field.
  const compareByField = (a, b, field, order) => {
    let fieldA = a[field];
    let fieldB = b[field];

    if (field === "orderDate") {
      fieldA = new Date(fieldA);
      fieldB = new Date(fieldB);
    } else if (field === "credits" || field === "amount") {
      fieldA = parseFloat(fieldA) || 0;
      fieldB = parseFloat(fieldB) || 0;
    } else if (field === "orderId") {
      // Compare orderId as numbers, if possible.
      fieldA = parseInt(fieldA, 10) || 0;
      fieldB = parseInt(fieldB, 10) || 0;
    } else {
      fieldA = (fieldA || "").toString().toLowerCase();
      fieldB = (fieldB || "").toString().toLowerCase();
    }
    if (fieldA < fieldB) return order === "asc" ? -1 : 1;
    if (fieldA > fieldB) return order === "asc" ? 1 : -1;
    return 0;
  };

  // Handle header clicks to trigger sorting by that field.
  const handleSort = (field) => {
    let order = "asc";
    if (sortField === field) {
      order = sortOrder === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortOrder(order);
    // Sort the orders using the new settings.
    const sortedOrders = [...orders].sort((a, b) =>
      compareByField(a, b, field, order)
    );
    setOrders(sortedOrders);
  };

  const fetchOrders = async (userId) => {
    //console.log("[Invoices] Fetching orders for userId:", userId);
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(process.env.REACT_APP_INVOICES_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId }),
        }
      );
      //console.log("[Invoices] Response status:", response.status);
      if (!response.ok) {
        throw new Error("Failed to fetch orders");
      }
      const data = await response.json();
      //console.log("[Invoices] Raw data from endpoint:", data);
      let rawItems = [];
      if (data.body) {
        try {
          const parsedBody = JSON.parse(data.body);
          rawItems = parsedBody.orders || [];
          //console.log("[Invoices] Parsed rawItems:", rawItems);
        } catch (parseErr) {
          //console.error("[Invoices] Error parsing data.body:", parseErr);
          setError("Invalid JSON in response body");
          setIsLoading(false);
          return;
        }
      } else {
        rawItems = data.orders || [];
        //console.log("[Invoices] Raw items from top-level data:", rawItems);
      }
      // Map and unify order data.
      const unified = rawItems.map((item) => {
        if (item.order) {
          return {
            ...item.order,
            userEmail: item.userEmail,
            settings: item.settings,
          };
        } else {
          return { ...item };
        }
      });
      // Sort orders using current sort settings.
      unified.sort((a, b) => compareByField(a, b, sortField, sortOrder));
      //console.log("[Invoices] Unified and sorted orders array:", unified);
      setOrders(unified);
    } catch (err) {
      setError(err.message);
      //console.error("[Invoices] fetchOrders Error:", err);
    } finally {
      setIsLoading(false);
      //console.log("[Invoices] Finished fetching orders.");
    }
  };

  const generatePDF = async (row) => {
    //console.log("[Invoices] generatePDF row:", row);
    if (!row || !row.orderId || !row.userId) {
      //console.error("[Invoices] Missing required fields (orderId or userId).");
      return;
    }
    try {
      const payload = {
        orderId: row.orderId,
        userId: row.userId,
      };
      const response = await fetch(process.env.REACT_APP_PRINT_INVOICE_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      //console.log("[Invoices] printInvoice response status:", response.status);
      if (!response.ok) {
        throw new Error("Failed to fetch invoice details");
      }
      const data = await response.json();
      //console.log("[Invoices] Raw data from printInvoice:", data);
      const parsedData = JSON.parse(data.body);
      const order = parsedData.order;
      const settings = parsedData.settings;
      const userEmail = parsedData.userEmail;
      if (!order || !order.orderId || !order.userId) {
        //console.error("[Invoices] Required invoice details missing in response");
        return;
      }
      // Extract customer details if available.
      const firstName = settings?.settings?.firstName?.value || "";
      const lastName = settings?.settings?.lastName?.value || "";
      const customerCompany = settings?.settings?.companyName?.value || "";

      // Create a PDF invoice using jsPDF.
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("Documore LLC", margin, 30);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text("82 Wendell Ave. STE 100,Pittsfield, MA, 01201, USA", margin, 40);
      doc.setLineWidth(0.5);
      doc.setDrawColor(255, 0, 0);
      doc.line(margin, 45, pageWidth - margin, 45);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("Invoice", pageWidth / 2, 60, { align: "center" });
      const leftX = margin;
      let currentY = 75;
      const lineSpacing = 8;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.text(`Invoice Number: ${order.orderId}`, leftX, currentY);
      currentY += lineSpacing;
      // Display the current invoice date. (Alternatively, you can use row.orderDate if required.)
      doc.text(`Invoice Date: ${new Date().toLocaleDateString()}`, leftX, currentY);
      currentY += lineSpacing;
      doc.text(`Status: ${order.status}`, leftX, currentY);
      currentY += lineSpacing;
      const rightX = pageWidth / 2 + 10;
      let rightY = 75;
      doc.text("Bill To:", rightX, rightY);
      doc.setFont("helvetica", "bold");
      rightY += lineSpacing;
      doc.text(`${firstName} ${lastName}`.trim(), rightX, rightY);
      rightY += lineSpacing;
      if (customerCompany) {
        doc.text(customerCompany, rightX, rightY);
        rightY += lineSpacing;
      }
      doc.setFont("helvetica", "normal");
      doc.text(userEmail, rightX, rightY);
      currentY += lineSpacing * 2;
      doc.setFont("helvetica", "bold");
      doc.text("Description", leftX, currentY);
      doc.text("Amount", pageWidth - margin - 30, currentY, { align: "right" });
      currentY += lineSpacing;
      doc.setLineWidth(0.2);
      doc.setDrawColor(0, 0, 0);
      doc.line(leftX, currentY, pageWidth - margin, currentY);
      currentY += lineSpacing;
      doc.setFont("helvetica", "normal");
      doc.text(`${order.option || "Invoice Payment"}`, leftX, currentY);
      doc.text(`$${order.amount || 0}`, pageWidth - margin - 30, currentY, { align: "right" });
      currentY += lineSpacing * 2;
      doc.setFont("helvetica", "bold");
      doc.text("Total", leftX, currentY);
      doc.text(`$${order.amount || 0}`, pageWidth - margin - 30, currentY, { align: "right" });
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 255);
      doc.line(margin, pageHeight - 40, pageWidth - margin, pageHeight - 40);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Thank you for your business!", pageWidth / 2, pageHeight - 30, { align: "center" });
      doc.text("If you have any questions about this invoice, please contact us.", pageWidth / 2, pageHeight - 20, { align: "center" });
      const pdfUrl = doc.output("bloburl");
      window.open(pdfUrl, "_blank");
    } catch (err) {
      //console.error("[Invoices] generatePDF Error:", err);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto mt-10 max-w-7xl bg-white shadow-md p-10 rounded-md">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Your Invoices</h1>
        {isLoading ? (
          <p className="text-gray-600">Loading orders...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : orders.length === 0 ? (
          <p className="text-gray-600">No confirmed orders found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed">
              <thead>
                <tr className="bg-gray-800 text-white">
                  <th
                    className="py-4 px-2 text-left cursor-pointer"
                    onClick={() => handleSort("orderId")}
                  >
                    Order ID {sortField === "orderId" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th
                    className="py-4 px-2 text-left cursor-pointer"
                    onClick={() => handleSort("orderDate")}
                  >
                    Date {sortField === "orderDate" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th
                    className="py-4 px-2 text-left cursor-pointer"
                    onClick={() => handleSort("credits")}
                  >
                    Credits {sortField === "credits" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                  </th>
                  <th
                    className="py-4 px-2 text-left cursor-pointer"
                    onClick={() => handleSort("amount")}
                  >
                    Amount {sortField === "amount" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                  </th>
                  
                  <th className="py-4 px-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map((row) => (
                  <tr key={row.orderId}>
                    <td className="py-3 px-2">{row.orderId}</td>
                    <td className="py-3 px-2">{convertUTCToLocal(row.orderDate)}</td>
                    <td className="py-3 px-2">{row.credits || "N/A"}</td>
                    <td className="py-3 px-2">{row.amount ? `$${row.amount}` : "N/A"}</td>
                    
                    <td className="py-3 px-2">
                      <button
                        onClick={() => generatePDF(row)}
                        className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 flex items-center space-x-2"
                      >
                        <img
                          src="/images/adobe-acrobat-logo.png"
                          alt="Adobe Acrobat PDF"
                          style={{ width: 20, height: 20 }}
                        />
                        <span>PDF</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Invoices;
